import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
    Plus,
    KeyRound,
    LogOut,
    Film,
    ArrowRight,
    MoreVertical,
    ExternalLink,
    Settings,
    Trash2,
    AlertTriangle,
    Loader2,
    Archive,
    ArchiveRestore,
    Sparkles,
    LayoutDashboard,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { productionTypeLabel } from "../lib/productionType";
import { getWorkspaceDestination, computeWorkspaceCardSubtitle } from "../lib/workspaceDestination";
import { useAuth } from "../context/AuthContext";
import { useApiBase } from "../context/ApiBaseContext";
import ThemeToggle from "../components/ThemeToggle";
import OnSetFlowWordmark from "../components/OnSetFlowWordmark";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
    DropdownMenu,
    DropdownMenuTrigger,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
} from "../components/ui/dropdown-menu";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "../components/ui/dialog";

export default function MyProjects() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const apiBase = useApiBase();
    const [projects, setProjects] = useState([]);
    const [archivedProjects, setArchivedProjects] = useState([]);
    // Iter-158.13.2.20f.4 — service-operator discovery. Non-operators
    // never see the entry point. Server-side gate is authoritative;
    // this is a rendering convenience only.
    const [isServiceOperator, setIsServiceOperator] = useState(false);
    // Iter-158.13.2 Front Door — beta departmental workspaces are
    // the primary destination for beta users. Each row = one
    // department the user has explicit access to (or owner-override
    // on their own production).
    const [betaWorkspaces, setBetaWorkspaces] = useState([]);
    // Iter-158.13.2.17 — Route 2 Production lifecycle housekeeping.
    // Same Active/Archived UX we've trusted for legacy, applied to
    // the upper PROJECTS section. The two lists are fetched
    // side-by-side on mount and re-fetched after every lifecycle
    // mutation (archive/unarchive/delete/leave). The selected mode
    // persists across refetches — we never bounce the user between
    // views.
    const [betaArchivedWorkspaces, setBetaArchivedWorkspaces] = useState([]);
    const [betaListMode, setBetaListMode] = useState("active"); // "active" | "archived"
    const [betaLifecycleBusyId, setBetaLifecycleBusyId] = useState(null);
    const [betaDeleteTarget, setBetaDeleteTarget] = useState(null);
    const [betaDeleteConfirmText, setBetaDeleteConfirmText] = useState("");
    const [betaDeleteBusy, setBetaDeleteBusy] = useState(false);
    const [betaDeleteError, setBetaDeleteError] = useState("");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    // Iter-135 — list view toggle. Defaults to Active.
    const [listMode, setListMode] = useState("active"); // "active" | "archived"
    // Iter-135 — archive/unarchive busy state per project id.
    const [archivingId, setArchivingId] = useState(null);
    // Iter-158.13.2 — auto-jump guard: only ever fires once per mount
    // so a manual click on the OnSetFlow logo won't re-trigger.
    const autoJumpedRef = useRef(false);

    // Delete dialog state — operates on one project at a time.
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [confirmText, setConfirmText] = useState("");
    const [deleting, setDeleting] = useState(false);
    const [deleteError, setDeleteError] = useState("");

    // Iter-158.13.2.20f.4 — probe service-operator status once on mount.
    // Silent 401/403 → non-operator; entry point stays hidden.
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.get("/service/status");
                if (!cancelled) setIsServiceOperator(!!data?.is_service_operator);
            } catch {
                if (!cancelled) setIsServiceOperator(false);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    useEffect(() => {
        (async () => {
            try {
                // Iter-135 — fetch legacy Active + Archived lists.
                // Iter-158.13.2 — additionally fetch beta departmental
                // workspaces (Front Door). 403 is expected for users
                // without beta access; swallow it and keep an empty list.
                // Iter-158.13.2.17 — fetch active AND archived beta
                // workspaces so the Route 2 Active/Archived toggle
                // doesn't need a second round-trip on switch.
                const [activeRes, archRes, wsActiveRes, wsArchRes] = await Promise.all([
                    api.get(apiBase),
                    api.get(apiBase, { params: { archived: "true" } }),
                    api.get("/beta/me/workspaces", { params: { archived: "false" } })
                        .catch(() => ({ data: { workspaces: [] } })),
                    api.get("/beta/me/workspaces", { params: { archived: "true" } })
                        .catch(() => ({ data: { workspaces: [] } })),
                ]);
                setProjects(activeRes.data);
                setArchivedProjects(archRes.data);
                setBetaWorkspaces(
                    Array.isArray(wsActiveRes?.data?.workspaces) ? wsActiveRes.data.workspaces : [],
                );
                setBetaArchivedWorkspaces(
                    Array.isArray(wsArchRes?.data?.workspaces) ? wsArchRes.data.workspaces : [],
                );
            } catch (e) {
                setError(formatApiError(e));
            } finally {
                setLoading(false);
            }
        })();
    }, [apiBase]);

    // Iter-158.13.2.17 — Route 2 workspace refetch. Called after
    // every lifecycle mutation. Preserves the current listMode so
    // the user never gets bounced between Active and Archived views.
    const refetchBetaWorkspaces = async () => {
        try {
            const [wsActiveRes, wsArchRes] = await Promise.all([
                api.get("/beta/me/workspaces", { params: { archived: "false" } })
                    .catch(() => ({ data: { workspaces: [] } })),
                api.get("/beta/me/workspaces", { params: { archived: "true" } })
                    .catch(() => ({ data: { workspaces: [] } })),
            ]);
            setBetaWorkspaces(
                Array.isArray(wsActiveRes?.data?.workspaces) ? wsActiveRes.data.workspaces : [],
            );
            setBetaArchivedWorkspaces(
                Array.isArray(wsArchRes?.data?.workspaces) ? wsArchRes.data.workspaces : [],
            );
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    // Iter-158.13.2 — Front Door auto-routing.
    // Users with EXACTLY one authorised destination land straight in
    // it: no confusing production/project picker in between. Fires
    // once per mount, only when the user has zero legacy projects
    // AND exactly one beta departmental workspace.
    //
    // Iter-158.13.2.1 — never auto-jump into a Production Hub row.
    // The hub is the top-level surface a production owner needs to
    // manage the production (invite people into departments, edit
    // canonical script, etc.); auto-jumping past it would defeat
    // the entire hub-and-spurs model. Owners always see the front
    // door; only single-dept-membership users auto-jump.
    //
    // Iter-158.13.2.6 — Explicit navigation intent ALWAYS wins.
    //   1. If a `?next=` / /join/beta/{code} journey preserved an
    //      invite target in sessionStorage but a downstream
    //      redirect (BetaGate flash / catch-all `*` route / stale
    //      token bounce) dropped the user at "/", resume that
    //      target instead of auto-jumping into a pre-existing sole
    //      department. This was the exact BT9 hijack bug in prod.
    //   2. The single-workspace auto-jump now only fires
    //      immediately after a successful login (osf_just_logged_in
    //      marker, consumed once). Manual visits to "/" — tapping
    //      the OnSetFlow wordmark, browser back, navigating home to
    //      review all authorised projects — NEVER auto-jump. The
    //      user can always reach My Projects and see every project
    //      they are entitled to access.
    useEffect(() => {
        if (loading) return;
        if (autoJumpedRef.current) return;

        // (1) Explicit invite intent wins over Front Door routing.
        let pendingTarget = null;
        try {
            pendingTarget = sessionStorage.getItem("osf_pending_invite_target");
        } catch { /* private mode */ }
        if (
            pendingTarget &&
            typeof pendingTarget === "string" &&
            pendingTarget.startsWith("/") &&
            !pendingTarget.startsWith("//")
        ) {
            autoJumpedRef.current = true;
            navigate(pendingTarget, { replace: true });
            return;
        }

        // (2) Consume-and-clear the just-logged-in marker. Only a
        // fresh sign-in unlocks the single-workspace auto-jump; any
        // subsequent manual visit to "/" behaves like an ordinary
        // navigation.
        let justLoggedIn = false;
        try {
            justLoggedIn = sessionStorage.getItem("osf_just_logged_in") === "1";
            if (justLoggedIn) sessionStorage.removeItem("osf_just_logged_in");
        } catch { /* private mode: never auto-jump, which is safe */ }
        if (!justLoggedIn) return;

        const explicitDept = betaWorkspaces.filter((w) => !w.is_hub);
        const hasHubRow = betaWorkspaces.some((w) => w.is_hub);
        if (hasHubRow) return;
        if (projects.length === 0 && explicitDept.length === 1) {
            const target = explicitDept[0]?.landing_url;
            if (target) {
                autoJumpedRef.current = true;
                navigate(target, { replace: true });
            }
        }
    }, [loading, projects, betaWorkspaces, navigate]);

    const visibleProjects = listMode === "archived" ? archivedProjects : projects;

    const onArchive = async (project) => {
        if (project.role !== "owner") return;
        if (!confirm(
            `Archive "${project.name}"?\n\n` +
            `The project will be removed from the active workspace but can ` +
            `be restored later by the project owner. All scenes, cast, ` +
            `notes, flags, captures and audit history are preserved.`,
        )) return;
        setArchivingId(project.id);
        try {
            await api.post(`${apiBase}/${project.id}/archive`);
            setProjects((prev) => prev.filter((p) => p.id !== project.id));
            setArchivedProjects((prev) => [
                { ...project, archived: true, archived_at: new Date().toISOString() },
                ...prev,
            ]);
            toast.success("Project archived. Find it under Archived.");
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setArchivingId(null);
        }
    };

    const onUnarchive = async (project) => {
        if (project.role !== "owner") return;
        setArchivingId(project.id);
        try {
            await api.post(`${apiBase}/${project.id}/unarchive`);
            setArchivedProjects((prev) => prev.filter((p) => p.id !== project.id));
            setProjects((prev) => [
                { ...project, archived: false, archived_at: null },
                ...prev,
            ]);
            toast.success("Project restored to active workspace.");
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setArchivingId(null);
        }
    };

    const openDelete = (project) => {
        setDeleteTarget(project);
        setConfirmText("");
        setDeleteError("");
    };

    const closeDelete = () => {
        if (deleting) return;
        setDeleteTarget(null);
        setConfirmText("");
        setDeleteError("");
    };

    const confirmDelete = async () => {
        if (!deleteTarget) return;
        if (confirmText.trim() !== deleteTarget.name) return;
        setDeleting(true);
        setDeleteError("");
        // Iter-157P.18.5 — delete resilience mirroring restore-point
        // create. Large projects (lots of scenes / media / restore
        // points) take >100s to delete server-side, the Cloudflare
        // edge cuts the connection, axios throws, and the producer
        // sees a raw "server response to Cloudflare could not parse"
        // error even though the backend kept working. We poll the
        // /projects list for up to 3 min and treat the target's
        // disappearance as success.
        const targetId = deleteTarget.id;
        const targetName = deleteTarget.name;
        const pollForDeletion = async () => {
            const MAX_ATTEMPTS = 36;  // 36 × 5s = 3 minutes
            const POLL_MS = 5000;
            for (let i = 0; i < MAX_ATTEMPTS; i++) {
                try {
                    const { data } = await api.get(apiBase);
                    const list = Array.isArray(data) ? data : [];
                    const stillThere = list.find((p) => p.id === targetId);
                    if (!stillThere) return true;
                } catch {
                    /* keep polling */
                }
                await new Promise((resolve) => setTimeout(resolve, POLL_MS));
            }
            return false;
        };
        const finalise = () => {
            toast.success(`Deleted "${targetName}"`, { duration: 8000 });
            setProjects((prev) => prev.filter((p) => p.id !== targetId));
            setDeleteTarget(null);
            setConfirmText("");
        };
        try {
            await api.delete(`${apiBase}/${targetId}`, {
                data: { confirm_name: confirmText.trim() },
                timeout: 120000,
            });
            finalise();
        } catch (e) {
            const gone = await pollForDeletion();
            if (gone) {
                finalise();
            } else {
                // Plain-English failure. Never the raw
                // Cloudflare/origin wording.
                setDeleteError(
                    "We couldn't delete this project. Please wait a moment and try again. If it keeps failing, your project may be too large for a single delete — contact support.",
                );
                toast.error("We couldn't delete this project.", {
                    description:
                        "Your project is safe — nothing has been removed. Please try again in a moment.",
                    duration: Infinity,
                });
            }
        } finally {
            setDeleting(false);
        }
    };

    const nameOk = deleteTarget && confirmText.trim() === deleteTarget.name;

    // ────────────────────────────────────────────────────────────
    // Iter-158.13.2.17 — Route 2 Production lifecycle handlers.
    // Authority is derived exclusively from Production scope
    // (`is_project_owner` on the workspace row; explicit dept role
    // has zero lifecycle influence). Every mutation ends with a
    // fresh `refetchBetaWorkspaces()` so the backend Production
    // lifecycle state is the source of truth — we never optimistically
    // hide/show sibling department cards.
    // ────────────────────────────────────────────────────────────
    const onBetaArchive = async (hubWorkspace) => {
        if (!hubWorkspace?.is_project_owner) return;
        if (!confirm(
            `Archive "${hubWorkspace.project_name}"?\n\n` +
            "A safety restore point is captured before the archive so " +
            "you can always come back. All data — script, scenes, cast, " +
            "continuity, media, notes, flags — is preserved."
        )) return;
        setBetaLifecycleBusyId(hubWorkspace.project_id);
        try {
            await api.post(`/beta/projects/${hubWorkspace.project_id}/archive`);
            await refetchBetaWorkspaces();
            toast.success(`"${hubWorkspace.project_name}" archived.`);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBetaLifecycleBusyId(null);
        }
    };

    const onBetaUnarchive = async (hubWorkspace) => {
        if (!hubWorkspace?.is_project_owner) return;
        setBetaLifecycleBusyId(hubWorkspace.project_id);
        try {
            await api.post(`/beta/projects/${hubWorkspace.project_id}/unarchive`);
            await refetchBetaWorkspaces();
            toast.success(`"${hubWorkspace.project_name}" restored to active.`);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBetaLifecycleBusyId(null);
        }
    };

    const onBetaLeave = async (hubWorkspace) => {
        // Non-owner self-leave. Owner MUST transfer first — enforced
        // by the shared team_access `/leave` factory. UI hides the
        // Leave entry for owners so this is defensive only.
        if (hubWorkspace?.is_project_owner) return;
        if (!confirm(
            `Leave "${hubWorkspace.project_name}"?\n\n` +
            "You'll lose access to every department workspace on this " +
            "Production. Other members are unaffected."
        )) return;
        setBetaLifecycleBusyId(hubWorkspace.project_id);
        try {
            await api.post(`/beta/hm-app/projects/${hubWorkspace.project_id}/leave`);
            await refetchBetaWorkspaces();
            toast.success(`Left "${hubWorkspace.project_name}".`);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBetaLifecycleBusyId(null);
        }
    };

    const openBetaDelete = (hubWorkspace) => {
        setBetaDeleteTarget(hubWorkspace);
        setBetaDeleteConfirmText("");
        setBetaDeleteError("");
    };

    const closeBetaDelete = () => {
        if (betaDeleteBusy) return;
        setBetaDeleteTarget(null);
        setBetaDeleteConfirmText("");
        setBetaDeleteError("");
    };

    const confirmBetaDelete = async () => {
        if (!betaDeleteTarget) return;
        const expected = (betaDeleteTarget.project_name || "").trim();
        if (betaDeleteConfirmText.trim() !== expected) return;
        setBetaDeleteBusy(true);
        setBetaDeleteError("");
        try {
            await api.delete(`/beta/projects/${betaDeleteTarget.project_id}`, {
                data: { confirm_name: betaDeleteConfirmText.trim() },
                timeout: 120000,
            });
            toast.success(`Deleted "${expected}"`, { duration: 8000 });
            await refetchBetaWorkspaces();
            setBetaDeleteTarget(null);
            setBetaDeleteConfirmText("");
        } catch (e) {
            setBetaDeleteError(formatApiError(e));
            toast.error("We couldn't delete this Production.", {
                description: "Nothing has been removed. Please try again.",
            });
        } finally {
            setBetaDeleteBusy(false);
        }
    };

    const betaDeleteNameOk =
        betaDeleteTarget &&
        betaDeleteConfirmText.trim() === (betaDeleteTarget.project_name || "").trim();

    // Which beta workspace list is currently visible? Same list of
    // workspaces (Hub + dept rows for one Production) always moves
    // through Active/Archived together — the backend serves the
    // atomic view; the FE just picks which one to render.
    const visibleBetaWorkspaces =
        betaListMode === "archived" ? betaArchivedWorkspaces : betaWorkspaces;

    return (
        <div data-testid="my-projects-page" className="min-h-screen grain">
            <header className="sticky top-0 z-50 backdrop-blur-xl bg-background/80 border-b border-white/10">
                <div className="max-w-2xl mx-auto px-5 h-16 flex items-center justify-between">
                    <div>
                        {/* Iter-158.13.2.20f.42-kiwi3 — legacy
                            eyebrow-style ONSETFLOW replaced by the
                            shared canonical <OnSetFlowWordmark/>
                            established in Kiwi #4. Same component
                            the login page and ProjectHeader use —
                            mixed-case OnSetFlow, OnSet in the
                            theme-appropriate primary foreground,
                            Flow grey, font-display font-black. The
                            "My Projects" subtitle below is preserved
                            unchanged as the page title. */}
                        <OnSetFlowWordmark
                            testid="myprojects-wordmark"
                            className="text-2xl"
                        />
                        <div className="font-display font-bold text-white text-lg leading-none mt-1">
                            My Projects
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        {isServiceOperator ? (
                            /* Iter-158.13.2.20f.4 — service-operator entry point.
                               Rendered ONLY when /service/status returns
                               is_service_operator:true (server-side gated).
                               Production Owners and Admins never see this. */
                            <Link
                                to="/service/production-access"
                                data-testid="myprojects-service-link"
                                className="tap inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.2em] px-3 h-8 rounded-full bg-sky-600 text-white border border-sky-400 hover:bg-sky-500 focus-visible:ring-2 focus-visible:ring-sky-300 focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                            >
                                Service
                            </Link>
                        ) : null}
                        <ThemeToggle testid="myprojects-theme-toggle" label={false} />
                        <button
                            data-testid="logout-button"
                            onClick={() => {
                                logout();
                                navigate("/login", { replace: true });
                            }}
                            className="tap inline-flex items-center gap-2 text-sm text-neutral-400 hover:text-white"
                        >
                            <LogOut size={16} />
                            <span className="hidden sm:inline">Sign out</span>
                        </button>
                    </div>
                </div>
            </header>

            <main className="max-w-2xl mx-auto px-5 pt-6 pb-32">
                <section className="mb-6">
                    <p className="text-sm text-neutral-400">
                        Hey <span className="text-white font-medium">{user?.name || "there"}</span>,
                        pick up where you left off or start something new.
                    </p>
                </section>

                <section className="grid grid-cols-2 gap-3 mb-8">
                    {/* Iter-136 — restrained "production paper tab"
                        tinting on the lifecycle CTAs. Soft green for
                        "create", lighter teal for "join". 2px left
                        border accent + a barely-there fill — calm,
                        not SaaS-decorated. */}
                    <Link
                        data-testid="create-project-cta"
                        to="/projects/new"
                        className="tap group rounded-xl border border-white/10 border-l-2 border-l-emerald-400/40 bg-emerald-500/[0.04] p-4 hover:bg-emerald-500/[0.07]"
                    >
                        <div className="flex items-center justify-between">
                            <div className="h-10 w-10 rounded-lg bg-emerald-500 text-white flex items-center justify-center">
                                <Plus size={18} />
                            </div>
                            <ArrowRight
                                size={16}
                                className="text-neutral-500 group-hover:text-emerald-300 transition-colors"
                            />
                        </div>
                        <div className="mt-4 font-semibold text-white">New project</div>
                        <div className="text-xs text-neutral-500 mt-1">Start from scratch</div>
                    </Link>
                    <Link
                        data-testid="join-project-cta"
                        to="/projects/join"
                        className="tap group rounded-xl border border-white/10 border-l-2 border-l-teal-400/40 bg-teal-500/[0.04] p-4 hover:bg-teal-500/[0.07]"
                    >
                        <div className="flex items-center justify-between">
                            <div className="h-10 w-10 rounded-lg bg-teal-500/85 text-white flex items-center justify-center">
                                <KeyRound size={18} />
                            </div>
                            <ArrowRight
                                size={16}
                                className="text-neutral-500 group-hover:text-teal-300 transition-colors"
                            />
                        </div>
                        <div className="mt-4 font-semibold text-white">Join project</div>
                        <div className="text-xs text-neutral-500 mt-1">
                            Enter a 6-character code
                        </div>
                    </Link>
                </section>

                {/* Iter-158.13.2 Front Door — beta departmental
                    workspaces the caller is authorised on. Each row
                    is a one-tap jump into the actual working
                    surface (no production → project → dept picker
                    chain). Multi-dept users pick from their
                    authorised choices. Hidden entirely when the
                    user has no beta workspaces.

                    Iter-158.13.2.1 — two shapes coexist:
                    • Production Hub rows (`is_hub=true`, dept_key=
                      null) render as the production-level entry
                      point for owners. Distinct fuchsia-to-amber
                      accent + LayoutDashboard icon.
                    • Department rows (`is_hub=false`) render as
                      before with a sparkle icon. */}
                {(betaWorkspaces.length > 0 || betaArchivedWorkspaces.length > 0) ? (
                    <section data-testid="my-departments-section" className="mb-8">
                        <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2">
                                <h2 className="text-xs uppercase tracking-[0.25em] text-neutral-500">
                                    Projects
                                </h2>
                                {/* Iter-158.13.2.17 — Active / Archived toggle for
                                    the upper PROJECTS section. Mirrors the
                                    legacy toggle beneath. Selected mode
                                    persists across mutations — archiving in
                                    Active hides the card in place; the user
                                    stays on Active. */}
                                <div
                                    data-testid="beta-projects-list-mode-toggle"
                                    className="ml-2 inline-flex items-center rounded-full border border-white/10 bg-card p-0.5"
                                >
                                    <button
                                        type="button"
                                        data-testid="beta-projects-list-mode-active"
                                        onClick={() => setBetaListMode("active")}
                                        className={`tap text-[10px] uppercase tracking-[0.2em] px-2.5 py-1 rounded-full transition-colors ${
                                            betaListMode === "active"
                                                ? "bg-secondary text-white"
                                                : "text-neutral-400 hover:text-neutral-200"
                                        }`}
                                    >
                                        Active
                                    </button>
                                    <button
                                        type="button"
                                        data-testid="beta-projects-list-mode-archived"
                                        onClick={() => setBetaListMode("archived")}
                                        className={`tap text-[10px] uppercase tracking-[0.2em] px-2.5 py-1 rounded-full transition-colors ${
                                            betaListMode === "archived"
                                                ? "bg-secondary text-white"
                                                : "text-neutral-400 hover:text-neutral-200"
                                        }`}
                                    >
                                        Archived
                                        {betaArchivedWorkspaces.length > 0 ? (
                                            <span
                                                data-testid="beta-projects-list-mode-archived-count"
                                                className="ml-1 text-[9px] text-neutral-500"
                                            >
                                                · {betaArchivedWorkspaces.length}
                                            </span>
                                        ) : null}
                                    </button>
                                </div>
                            </div>
                            <span
                                data-testid="my-departments-count"
                                className="text-xs text-neutral-500"
                            >
                                {visibleBetaWorkspaces.length}
                            </span>
                        </div>
                        {visibleBetaWorkspaces.length === 0 ? (
                            <div
                                data-testid={
                                    betaListMode === "archived"
                                        ? "beta-projects-archived-empty"
                                        : "beta-projects-empty"
                                }
                                className="rounded-xl border border-dashed border-white/10 p-8 text-center"
                            >
                                <Film size={22} className="mx-auto text-neutral-500" />
                                <p className="mt-3 text-neutral-400 text-sm">
                                    {betaListMode === "archived"
                                        ? "No archived Productions yet."
                                        : "No active Productions yet."}
                                </p>
                            </div>
                        ) : (
                        (() => {
                            // Iter-158.13.2.20f.2 #1 — split the list so
                            // the WORKING destinations (department cards
                            // — the surfaces the user actually opens to
                            // do work) render at the top of the section,
                            // and the PRODUCTION MANAGEMENT hub rows
                            // render as a distinct, subordinate cluster
                            // below. The two groups are still driven by
                            // the same `is_hub` flag as before; the
                            // lifecycle menu remains hub-only. No new
                            // testids, no new state — just a render
                            // reorder inside the same section.
                            const deptRows = visibleBetaWorkspaces.filter((w) => !w.is_hub);
                            const hubRows = visibleBetaWorkspaces.filter((w) => Boolean(w.is_hub));
                            const renderRow = (w) => {
                                const isHub = Boolean(w.is_hub);
                                const key = isHub
                                    ? `hub-${w.project_id}`
                                    : `${w.project_id}-${w.dept_key || "production"}`;
                                const primaryTitle = w.project_name;
                                const { destination, permission, testKey } =
                                    getWorkspaceDestination(w);
                                const isProdDest = testKey === "production";
                                const subtitle = computeWorkspaceCardSubtitle(w);
                                const isBusy = betaLifecycleBusyId === w.project_id;
                                const isArchivedRow = Boolean(w.archived);
                                return (
                                    <li key={key} className="relative">
                                        <Link
                                            data-testid={
                                                isHub
                                                    ? `production-hub-card-${w.project_id}`
                                                    : (isProdDest
                                                        ? `production-destination-card-${w.project_id}`
                                                        : `department-card-${key}`)
                                            }
                                            data-destination={testKey}
                                            to={w.landing_url}
                                            className={`tap group flex items-center gap-3 rounded-xl border border-white/10 border-l-2 p-4 ${
                                                isProdDest
                                                    ? "border-l-amber-400/50 bg-amber-500/[0.04] hover:bg-amber-500/[0.07]"
                                                    : "border-l-fuchsia-400/40 bg-fuchsia-500/[0.03] hover:bg-fuchsia-500/[0.06]"
                                            } ${isArchivedRow ? "opacity-70" : ""}`}
                                        >
                                            <div
                                                className={`h-12 w-12 rounded-lg flex items-center justify-center text-white ${
                                                    isProdDest
                                                        ? "bg-amber-500/85"
                                                        : "bg-fuchsia-500/85"
                                                }`}
                                            >
                                                {isProdDest ? (
                                                    <LayoutDashboard size={18} />
                                                ) : (
                                                    <Sparkles size={18} />
                                                )}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="font-semibold text-white truncate">
                                                    {primaryTitle}
                                                </div>
                                                <div
                                                    className="text-xs text-neutral-300 truncate"
                                                    data-testid={`workspace-destination-${w.project_id}-${testKey}`}
                                                >
                                                    {subtitle}
                                                </div>
                                                <div className="text-[11px] text-neutral-500 truncate mt-0.5">
                                                    {permission || "\u00A0"}
                                                    {isArchivedRow ? (
                                                        <span className="ml-1 text-neutral-500">
                                                            · Archived
                                                        </span>
                                                    ) : null}
                                                </div>
                                            </div>
                                            <ArrowRight
                                                size={18}
                                                className={`text-neutral-500 group-hover:text-white transition-colors ${
                                                    (isHub || (!isHub && !w.is_project_owner)) ? "mr-8" : ""
                                                }`}
                                            />
                                        </Link>
                                        {/* Iter-158.13.2.20f.3 #8 — non-owner Leave
                                            affordance on ANY row (hub or department).
                                            Hub rows already have the full lifecycle
                                            menu; department rows now expose a light
                                            menu carrying JUST the Leave action for
                                            non-owners. Production Owners never see
                                            Leave here (must transfer first — enforced
                                            server-side). Reuses the exact same
                                            `/beta/hm-app/projects/:pid/leave` endpoint
                                            the hub menu uses. */}
                                        {isHub ? (
                                            <div
                                                className="absolute right-3 top-1/2 -translate-y-1/2"
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                <DropdownMenu>
                                                    <DropdownMenuTrigger asChild>
                                                        <button
                                                            type="button"
                                                            data-testid={`beta-hub-menu-${w.project_id}`}
                                                            disabled={isBusy}
                                                            className="tap p-1.5 rounded-md hover:bg-white/10 text-neutral-400 hover:text-white"
                                                            onClick={(e) => e.preventDefault()}
                                                        >
                                                            {isBusy ? (
                                                                <Loader2 size={16} className="animate-spin" />
                                                            ) : (
                                                                <MoreVertical size={16} />
                                                            )}
                                                        </button>
                                                    </DropdownMenuTrigger>
                                                    <DropdownMenuContent align="end" className="w-52">
                                                        <DropdownMenuItem
                                                            data-testid={`beta-hub-open-${w.project_id}`}
                                                            onClick={() => navigate(w.landing_url)}
                                                        >
                                                            <ExternalLink size={14} className="mr-2" />
                                                            Open Production
                                                        </DropdownMenuItem>
                                                        {w.is_project_owner ? (
                                                            <DropdownMenuItem
                                                                data-testid={`beta-hub-settings-${w.project_id}`}
                                                                onClick={() =>
                                                                    navigate(`/beta/projects/${w.project_id}/settings`)
                                                                }
                                                            >
                                                                <Settings size={14} className="mr-2" />
                                                                Production settings
                                                            </DropdownMenuItem>
                                                        ) : null}
                                                        <DropdownMenuSeparator />
                                                        {w.is_project_owner && !isArchivedRow ? (
                                                            <DropdownMenuItem
                                                                data-testid={`beta-hub-archive-${w.project_id}`}
                                                                onClick={() => onBetaArchive(w)}
                                                            >
                                                                <Archive size={14} className="mr-2" />
                                                                Archive Production
                                                            </DropdownMenuItem>
                                                        ) : null}
                                                        {w.is_project_owner && isArchivedRow ? (
                                                            <DropdownMenuItem
                                                                data-testid={`beta-hub-unarchive-${w.project_id}`}
                                                                onClick={() => onBetaUnarchive(w)}
                                                            >
                                                                <ArchiveRestore size={14} className="mr-2" />
                                                                Unarchive Production
                                                            </DropdownMenuItem>
                                                        ) : null}
                                                        {w.is_project_owner ? (
                                                            <DropdownMenuItem
                                                                data-testid={`beta-hub-delete-${w.project_id}`}
                                                                onClick={() => openBetaDelete(w)}
                                                                className="text-red-400 focus:text-red-300"
                                                            >
                                                                <Trash2 size={14} className="mr-2" />
                                                                Delete Production
                                                            </DropdownMenuItem>
                                                        ) : (
                                                            <DropdownMenuItem
                                                                data-testid={`beta-hub-leave-${w.project_id}`}
                                                                onClick={() => onBetaLeave(w)}
                                                                className="text-red-400 focus:text-red-300"
                                                            >
                                                                <LogOut size={14} className="mr-2" />
                                                                Leave Production
                                                            </DropdownMenuItem>
                                                        )}
                                                    </DropdownMenuContent>
                                                </DropdownMenu>
                                            </div>
                                        ) : !w.is_project_owner ? (
                                            <div
                                                className="absolute right-3 top-1/2 -translate-y-1/2"
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                <DropdownMenu>
                                                    <DropdownMenuTrigger asChild>
                                                        <button
                                                            type="button"
                                                            data-testid={`beta-dept-menu-${key}`}
                                                            disabled={isBusy}
                                                            className="tap p-1.5 rounded-md hover:bg-white/10 text-neutral-400 hover:text-white"
                                                            onClick={(e) => e.preventDefault()}
                                                        >
                                                            {isBusy ? (
                                                                <Loader2 size={16} className="animate-spin" />
                                                            ) : (
                                                                <MoreVertical size={16} />
                                                            )}
                                                        </button>
                                                    </DropdownMenuTrigger>
                                                    <DropdownMenuContent align="end" className="w-52">
                                                        <DropdownMenuItem
                                                            data-testid={`beta-dept-open-${key}`}
                                                            onClick={() => navigate(w.landing_url)}
                                                        >
                                                            <ExternalLink size={14} className="mr-2" />
                                                            Open
                                                        </DropdownMenuItem>
                                                        <DropdownMenuSeparator />
                                                        <DropdownMenuItem
                                                            data-testid={`beta-dept-leave-${key}`}
                                                            onClick={() => onBetaLeave(w)}
                                                            className="text-red-400 focus:text-red-300"
                                                        >
                                                            <LogOut size={14} className="mr-2" />
                                                            Leave Production
                                                        </DropdownMenuItem>
                                                    </DropdownMenuContent>
                                                </DropdownMenu>
                                            </div>
                                        ) : null}
                                    </li>
                                );
                            };
                            return (
                                <>
                                    {deptRows.length > 0 ? (
                                        <ul
                                            data-testid="my-departments-working"
                                            className="space-y-3"
                                        >
                                            {deptRows.map(renderRow)}
                                        </ul>
                                    ) : null}
                                    {hubRows.length > 0 ? (
                                        <div
                                            data-testid="my-departments-management"
                                            className={
                                                deptRows.length > 0
                                                    /* Iter-158.13.2.20f.32 —
                                                       Strong visual break
                                                       between PROJECTS and
                                                       PRODUCTION MANAGEMENT
                                                       (SCRIPT CHANGES).
                                                       Carla UAT: the class
                                                       distinction was
                                                       insufficient with the
                                                       previous mt-8 / pt-6
                                                       border-t border-white/10
                                                       divider. Increase
                                                       vertical breathing room
                                                       (mt-14 pt-10) and
                                                       heavier rule
                                                       (border-t-2 border-
                                                       white/25) so the two
                                                       access classes read as
                                                       unmistakably separate
                                                       without becoming
                                                       decorative. */
                                                    ? "mt-14 pt-10 border-t-2 border-white/25"
                                                    : ""
                                            }
                                        >
                                            <div className="mb-3 flex items-center gap-2">
                                                <span
                                                    data-testid="my-departments-management-heading"
                                                    className="text-[11px] uppercase tracking-[0.28em] text-neutral-300 font-semibold"
                                                >
                                                    Production Management (Script Changes)
                                                </span>
                                                <span className="h-px flex-1 bg-white/10" />
                                            </div>
                                            <ul className="space-y-3">
                                                {hubRows.map(renderRow)}
                                            </ul>
                                        </div>
                                    ) : null}
                                </>
                            );
                        })()
                        )}
                    </section>
                ) : null}

                <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                        <h2 className="text-xs uppercase tracking-[0.25em] text-neutral-500">
                            Projects (Legacy)
                        </h2>
                        {/* Iter-135 — Active / Archived toggle. Calm
                            two-state pill, archived list visually
                            quieter than active so the active workspace
                            stays the default focus. */}
                        <div
                            data-testid="projects-list-mode-toggle"
                            className="ml-2 inline-flex items-center rounded-full border border-white/10 bg-card p-0.5"
                        >
                            <button
                                type="button"
                                data-testid="projects-list-mode-active"
                                onClick={() => setListMode("active")}
                                className={`tap text-[10px] uppercase tracking-[0.2em] px-2.5 py-1 rounded-full transition-colors ${
                                    listMode === "active"
                                        ? "bg-secondary text-white"
                                        : "text-neutral-400 hover:text-neutral-200"
                                }`}
                            >
                                Active
                            </button>
                            <button
                                type="button"
                                data-testid="projects-list-mode-archived"
                                onClick={() => setListMode("archived")}
                                className={`tap text-[10px] uppercase tracking-[0.2em] px-2.5 py-1 rounded-full transition-colors ${
                                    listMode === "archived"
                                        ? "bg-secondary text-white"
                                        : "text-neutral-400 hover:text-neutral-200"
                                }`}
                            >
                                Archived
                                {archivedProjects.length > 0 ? (
                                    <span
                                        data-testid="projects-list-mode-archived-count"
                                        className="ml-1 text-[9px] text-neutral-500"
                                    >
                                        · {archivedProjects.length}
                                    </span>
                                ) : null}
                            </button>
                        </div>
                    </div>
                    <span
                        data-testid="project-count"
                        className="text-xs text-neutral-500"
                    >
                        {visibleProjects.length}
                    </span>
                </div>

                {loading ? (
                    <div data-testid="projects-loading" className="space-y-3">
                        {[0, 1, 2].map((i) => (
                            <div
                                key={i}
                                className="h-20 rounded-xl bg-card border border-white/10 animate-pulse"
                            />
                        ))}
                    </div>
                ) : error ? (
                    <div data-testid="projects-error" className="text-sm text-red-400">
                        {error}
                    </div>
                ) : visibleProjects.length === 0 &&
                  !(
                      listMode !== "archived" &&
                      betaWorkspaces.some(
                          (w) => w.is_hub && w.is_project_owner,
                      )
                  ) ? (
                    <div
                        data-testid={listMode === "archived" ? "projects-archived-empty" : "projects-empty"}
                        className="rounded-xl border border-dashed border-white/10 p-8 text-center"
                    >
                        <Film size={22} className="mx-auto text-neutral-500" />
                        {listMode === "archived" ? (
                            <p className="mt-3 text-neutral-400 text-sm">
                                No archived projects yet. Wrapped productions
                                land here for future pickup, reshoot, or
                                reference access.
                            </p>
                        ) : betaWorkspaces.length > 0 ? (
                            /* Iter-158.13.2.9 — Front Door correction. A user
                               who already belongs to department workspaces
                               is NOT a first-time visitor. The generic
                               "Create your first production" copy + duplicate
                               Create button read as if they'd never touched
                               the app. Show a scoped, non-CTA notice; the
                               real "New project" affordance already sits at
                               the top of the page (line ~294).

                               Iter-158.13.2.10 — When the caller owns at
                               least one beta Production Hub the outer
                               conditional above short-circuits and this
                               empty container is not rendered at all — the
                               visible hub card directly above already proves
                               ownership. This branch therefore only fires
                               for dept-only users (no owned hub) — the
                               original 13.2.9 intent. */
                            <p
                                data-testid="projects-empty-owner-scope"
                                className="mt-3 text-neutral-400 text-sm"
                            >
                                No productions you own yet.
                                <br />
                                <span className="text-neutral-500">
                                    Productions you create or take ownership of will appear here.
                                </span>
                            </p>
                        ) : (
                            <>
                                <p className="mt-3 text-neutral-400 text-sm">
                                    No projects yet. Create your first production to get the
                                    whole team on the same page.
                                </p>
                                <Button
                                    data-testid="empty-create-btn"
                                    asChild
                                    className="mt-5 min-tap"
                                >
                                    <Link to="/projects/new">Create first project</Link>
                                </Button>
                            </>
                        )}
                    </div>
                ) : visibleProjects.length === 0 ? (
                    /* Iter-158.13.2.10 — Beta hub owner with zero legacy
                       projects: skip the misleading empty state entirely.
                       Their Production Hub card is rendered above in the
                       workspaces section. */
                    null
                ) : (
                    <ul className="space-y-3">
                        {visibleProjects.map((p) => (
                            <li key={p.id}>
                                <div
                                    data-testid={`project-card-${p.id}`}
                                    className={`group relative flex items-center gap-3 rounded-xl border ${
                                        listMode === "archived"
                                            ? "border-white/5 border-l-2 border-l-neutral-500/30 bg-card/40 opacity-90"
                                            : "border-white/10 border-l-2 border-l-sky-400/40 bg-sky-500/[0.03] hover:bg-sky-500/[0.06]"
                                    }`}
                                >
                                    <Link
                                        data-testid={`project-card-link-${p.id}`}
                                        to={`/projects/${p.id}`}
                                        className="tap flex flex-1 min-w-0 items-center gap-4 p-4"
                                    >
                                        <div className="h-12 w-12 rounded-lg bg-secondary flex items-center justify-center text-white font-display font-bold">
                                            {p.name.slice(0, 1).toUpperCase()}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="font-semibold text-white truncate">
                                                {p.name}
                                            </div>
                                            <div className="text-xs text-neutral-500 truncate">
                                                {productionTypeLabel(p.production_type)}
                                                {" · "}
                                                {p.member_count} member
                                                {p.member_count === 1 ? "" : "s"}
                                                {" · "}
                                                <span className="uppercase tracking-wider">
                                                    {p.role}
                                                </span>
                                            </div>
                                        </div>
                                        <ArrowRight
                                            size={18}
                                            className="text-neutral-500 group-hover:text-white transition-colors"
                                        />
                                    </Link>

                                    <DropdownMenu>
                                        <DropdownMenuTrigger asChild>
                                            <button
                                                type="button"
                                                data-testid={`project-menu-trigger-${p.id}`}
                                                aria-label={`Actions for ${p.name}`}
                                                className="tap min-tap shrink-0 mr-2 inline-flex items-center justify-center rounded-md h-11 w-11 text-neutral-400 hover:text-white hover:bg-white/5"
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                <MoreVertical size={18} />
                                            </button>
                                        </DropdownMenuTrigger>
                                        <DropdownMenuContent
                                            data-testid={`project-menu-${p.id}`}
                                            align="end"
                                            className="w-56 bg-popover text-neutral-200 border-white/10 shadow-2xl"
                                        >
                                            <DropdownMenuItem
                                                data-testid={`project-menu-open-${p.id}`}
                                                onClick={() => navigate(`/projects/${p.id}`)}
                                                className="text-neutral-200 focus:bg-white/5 focus:text-white"
                                            >
                                                <ExternalLink size={14} className="mr-2" />
                                                Open project
                                            </DropdownMenuItem>
                                            <DropdownMenuItem
                                                data-testid={`project-menu-settings-${p.id}`}
                                                onClick={() => navigate(`/projects/${p.id}/settings`)}
                                                className="text-neutral-200 focus:bg-white/5 focus:text-white"
                                            >
                                                <Settings size={14} className="mr-2" />
                                                Project settings
                                            </DropdownMenuItem>
                                            {/* Iter-135 — Archive / Restore lifecycle.
                                                Owner-only; never destructive. */}
                                            {p.role === "owner" && !p.archived ? (
                                                <>
                                                    <DropdownMenuSeparator className="bg-white/10" />
                                                    <DropdownMenuItem
                                                        data-testid={`project-menu-archive-${p.id}`}
                                                        onClick={() => onArchive(p)}
                                                        disabled={archivingId === p.id}
                                                        className="text-neutral-200 focus:bg-white/5 focus:text-white"
                                                    >
                                                        <Archive size={14} className="mr-2" />
                                                        Archive project
                                                    </DropdownMenuItem>
                                                </>
                                            ) : null}
                                            {p.role === "owner" && p.archived ? (
                                                <>
                                                    <DropdownMenuSeparator className="bg-white/10" />
                                                    <DropdownMenuItem
                                                        data-testid={`project-menu-unarchive-${p.id}`}
                                                        onClick={() => onUnarchive(p)}
                                                        disabled={archivingId === p.id}
                                                        className="text-emerald-300 focus:bg-emerald-500/10 focus:text-emerald-200"
                                                    >
                                                        <ArchiveRestore size={14} className="mr-2" />
                                                        Restore project
                                                    </DropdownMenuItem>
                                                </>
                                            ) : null}
                                            {p.role === "owner" ? (
                                                <>
                                                    <DropdownMenuSeparator className="bg-white/10" />
                                                    <DropdownMenuItem
                                                        data-testid={`project-menu-delete-${p.id}`}
                                                        onClick={() => openDelete(p)}
                                                        className="text-red-300 focus:bg-red-500/10 focus:text-red-200"
                                                    >
                                                        <Trash2 size={14} className="mr-2" />
                                                        Delete project
                                                    </DropdownMenuItem>
                                                </>
                                            ) : null}
                                        </DropdownMenuContent>
                                    </DropdownMenu>
                                </div>
                            </li>
                        ))}
                    </ul>
                )}
            </main>

            <Dialog
                open={Boolean(deleteTarget)}
                onOpenChange={(v) => (!v ? closeDelete() : null)}
            >
                <DialogContent
                    data-testid="my-projects-delete-dialog"
                    className="bg-card border-white/10"
                >
                    <DialogHeader>
                        <DialogTitle className="font-display text-xl font-bold text-white flex items-center gap-2">
                            <AlertTriangle size={18} className="text-red-400" />
                            Delete project
                        </DialogTitle>
                        <DialogDescription className="text-neutral-400">
                            This permanently deletes{" "}
                            <span className="text-white font-semibold">
                                {deleteTarget?.name}
                            </span>{" "}
                            and all its scenes, characters, media and flags. This cannot
                            be undone.
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-3 pt-2">
                        <Label className="text-xs uppercase tracking-[0.2em] text-neutral-500">
                            Type the project name to confirm
                        </Label>
                        <Input
                            data-testid="my-projects-delete-confirm-input"
                            value={confirmText}
                            onChange={(e) => setConfirmText(e.target.value)}
                            placeholder={deleteTarget?.name || ""}
                            autoFocus
                            className="h-12 bg-secondary border-transparent focus-visible:ring-1 focus-visible:ring-red-400"
                        />
                        {deleteError ? (
                            <p
                                data-testid="my-projects-delete-error"
                                className="text-xs text-red-400"
                            >
                                {deleteError}
                            </p>
                        ) : null}

                        <div className="flex items-center gap-2 pt-2">
                            <Button
                                data-testid="my-projects-delete-cancel"
                                type="button"
                                variant="secondary"
                                disabled={deleting}
                                onClick={closeDelete}
                                className="flex-1 min-tap"
                            >
                                Cancel
                            </Button>
                            <Button
                                data-testid="my-projects-delete-confirm"
                                type="button"
                                disabled={!nameOk || deleting}
                                onClick={confirmDelete}
                                className="flex-1 min-tap bg-red-500 hover:bg-red-500/90 text-white font-semibold"
                            >
                                {deleting ? (
                                    <>
                                        <Loader2
                                            size={14}
                                            className="mr-2 animate-spin"
                                        />
                                        Deleting…
                                    </>
                                ) : (
                                    <>
                                        <Trash2 size={14} className="mr-2" />
                                        Delete
                                    </>
                                )}
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Iter-158.13.2.17 — Route 2 Production destructive delete
                dialog. Reuses the legacy Dialog + confirm_name UX
                verbatim so users don't learn a second deletion
                affordance. Distinct data-testids so the testing
                agent can target either surface. */}
            <Dialog
                open={Boolean(betaDeleteTarget)}
                onOpenChange={(v) => (!v ? closeBetaDelete() : null)}
            >
                <DialogContent
                    data-testid="beta-projects-delete-dialog"
                    className="bg-card border-white/10"
                >
                    <DialogHeader>
                        <DialogTitle className="font-display text-xl font-bold text-white flex items-center gap-2">
                            <AlertTriangle size={18} className="text-red-400" />
                            Delete Production
                        </DialogTitle>
                        <DialogDescription className="text-neutral-400">
                            This permanently deletes{" "}
                            <span className="text-white font-semibold">
                                {betaDeleteTarget?.project_name}
                            </span>{" "}
                            and every associated department workspace, script,
                            scene, character, continuity note, image, flag and
                            audit record. This cannot be undone.
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-3 pt-2">
                        <Label className="text-xs uppercase tracking-[0.2em] text-neutral-500">
                            Type the Production name to confirm
                        </Label>
                        <Input
                            data-testid="beta-projects-delete-confirm-input"
                            value={betaDeleteConfirmText}
                            onChange={(e) => setBetaDeleteConfirmText(e.target.value)}
                            placeholder={betaDeleteTarget?.project_name || ""}
                            autoFocus
                            className="h-12 bg-secondary border-transparent focus-visible:ring-1 focus-visible:ring-red-400"
                        />
                        {betaDeleteError ? (
                            <p
                                data-testid="beta-projects-delete-error"
                                className="text-xs text-red-400"
                            >
                                {betaDeleteError}
                            </p>
                        ) : null}

                        <div className="flex items-center gap-2 pt-2">
                            <Button
                                data-testid="beta-projects-delete-cancel"
                                type="button"
                                variant="secondary"
                                disabled={betaDeleteBusy}
                                onClick={closeBetaDelete}
                                className="flex-1 min-tap"
                            >
                                Cancel
                            </Button>
                            <Button
                                data-testid="beta-projects-delete-confirm"
                                type="button"
                                disabled={!betaDeleteNameOk || betaDeleteBusy}
                                onClick={confirmBetaDelete}
                                className="flex-1 min-tap bg-red-500 hover:bg-red-500/90 text-white font-semibold"
                            >
                                {betaDeleteBusy ? (
                                    <>
                                        <Loader2 size={14} className="mr-2 animate-spin" />
                                        Deleting…
                                    </>
                                ) : (
                                    <>
                                        <Trash2 size={14} className="mr-2" />
                                        Delete
                                    </>
                                )}
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}
